import React, { FormEvent, useState, useEffect } from 'react'
import * as Sentry from '@sentry/browser'
import { yupResolver } from '@hookform/resolvers/yup'
import { flowRight as compose, isEmpty } from 'lodash'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import { useHistory } from 'react-router-dom'
import { loaderState } from 'recoils/atoms'

import type { CompanyForForm, CompanyForFormSubmit } from 'types'
import { Button, PromptGuard, Modal } from 'components'
import { Confirmation, ModalSuccess } from 'components/Modal'
import {
  CompanyService,
  CompanyForm,
  initialCompanyForm,
  schemaCompanyForm,
  formatForm,
  BREADCRUMBS_CREATE,
} from 'features/Company'
import { withBackOfficeCms } from 'helpers/hocs'
import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromptForm,
} from 'helpers/hooks'
import { routes } from 'helpers/routes'

function CompanyCreateContainer(): JSX.Element {
  const history = useHistory()
  const methods = useForm<CompanyForForm>({
    defaultValues: initialCompanyForm,
    resolver: yupResolver(schemaCompanyForm),
  })
  const [shouldPrompt, setShouldPrompt] = usePromptForm<CompanyForForm>(methods)
  const [isLoadingForm] = useFormSubmitLoader<CompanyForForm>(methods)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [payload, setPayload] = useState<CompanyForFormSubmit>()
  const setLoader = useSetRecoilState(loaderState)
  const { t } = useTranslation(['layout', 'common'])

  useBreadcrumbs(BREADCRUMBS_CREATE)

  const callApi = async () => {
    if (payload) {
      try {
        setLoader(true)

        await CompanyService.create(payload, handleOpenSuccess)
        setLoader(false)
      } catch (error) {
        Sentry.captureException(error)
        setLoader(false)
      }
    }
  }

  const beforeSubmit = (e: FormEvent<HTMLFormElement>) => {
    methods.setValue('currentState', 'onSubmit')
    methods.handleSubmit(setData)(e)
    methods.setValue('currentState', '')
  }

  const setAddressLoading = (loading: boolean) => {
    setIsLoading(loading)
  }

  const handleBack = (): void => {
    history.push(routes.company.path)
  }

  const setData = (data: CompanyForFormSubmit) => {
    const payload = formatForm(data)
    if (isEmpty(methods.errors) && payload) {
      setIsOpenConfirm(true)
      setPayload(payload)
    }
  }

  const renderConfirmModal = () => {
    return (
      isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.title.label')}
            description={t('common:confirmation.description.label')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={callApi}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )
    )
  }

  const renderSuccessModal = () => {
    return (
      isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.label')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )
    )
  }

  const handleOpenSuccess = async () => {
    setIsOpenSuccess(true)

    await setTimeout(() => {
      setShouldPrompt(false)
      handleBack()
    }, 2000)
  }

  useEffect(() => {
    if (isOpenSuccess) {
      handleOpenSuccess()
    }
  }, [isOpenSuccess])

  return (
    <div className='container__form'>
      <PromptGuard when={shouldPrompt} />
      <FormProvider {...methods}>
        <form onSubmit={beforeSubmit}>
          <CompanyForm setAddressLoading={setAddressLoading} />
          <div className='container__form__actions'>
            <Button variants='secondary' onClick={handleBack}>
              {t('common:cancel.button')}
            </Button>
            <Button type='submit' isLoading={isLoading || isLoadingForm}>
              {t('common:save.button')}
            </Button>
          </div>
        </form>
      </FormProvider>

      {renderConfirmModal()}
      {renderSuccessModal()}
    </div>
  )
}

export default compose(withBackOfficeCms)(CompanyCreateContainer)
